// src/components/tokenPage/risk/TailMetricsPanel.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { MetricBox } from "@/components/tokenPage/shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import { useToken } from "@/contexts";
import { useTokenPrice } from "@/contexts/token";
import {
  valueAtRisk,
  expectedShortfall,
  skewness,
  kurtosis,
  crashSensitivity,
  medianRecoveryTime,
} from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';
const RANGE_TO_DAYS: Record<Range, number> = { '90d': 90, '180d': 180, '365d': 365, 'All': Infinity };

const TailMetricsPanel: React.FC = () => {
  const { ticker } = useToken();
  const [range, setRange] = useState<Range>('365d');

  const { token, benchmarks, isLoading } = useTokenPrice();
  const returns = token.returns;
  const btcReturnsData = benchmarks['BTC']?.returns;
  const prices = token.prices;

  const { var95Pct, es95Pct, skew, kurt, crashSensitivityPct, medianRecoveryDays } = useMemo(() => {
    if (returns.length === 0) {
      return { 
        var95Pct: null, 
        es95Pct: null, 
        skew: null, 
        kurt: null, 
        crashSensitivityPct: null, 
        medianRecoveryDays: null 
      };
    }

    const maxPoints = RANGE_TO_DAYS[range];
    const windowReturns = maxPoints === Infinity 
      ? returns 
      : returns.slice(-maxPoints);
    
    const btcReturns = btcReturnsData || [];
    const windowBtcReturns = maxPoints === Infinity 
      ? btcReturns 
      : btcReturns.slice(-maxPoints);
    
    const windowPrices = maxPoints === Infinity 
      ? prices.values 
      : prices.values.slice(-maxPoints);

    // VaR and ES
    const v95 = valueAtRisk(windowReturns, 0.95);
    const var95Pct = v95 !== null ? v95 * 100 : null;
    const es95 = expectedShortfall(windowReturns, 0.95);
    const es95Pct = es95 !== null ? es95 * 100 : null;

    // Skewness and Kurtosis
    const skew = skewness(windowReturns);
    const kurt = kurtosis(windowReturns);

    // Crash sensitivity
    const crashSens = crashSensitivity(windowReturns, windowBtcReturns, 10);
    const crashSensitivityPct = crashSens !== null ? crashSens * 100 : null;

    // Median recovery time
    const medianRecoveryDays = medianRecoveryTime(windowPrices);

    return { var95Pct, es95Pct, skew, kurt, crashSensitivityPct, medianRecoveryDays };
  }, [returns, btcReturnsData, prices.values, range]);

  // Hide if no data available (after loading)
  const hasData = var95Pct != null || es95Pct != null || skew != null || kurt != null || crashSensitivityPct != null || medianRecoveryDays != null;
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Tail Metrics"
      subtitle="VaR/ES, higher moments, crash and recovery"
      icon="triangle-alert"
      iconColor="text-amber-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} />
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <MetricBox label={`VaR 95% (${range})`} value={var95Pct} fmt="pct2" toneOverride={var95Pct != null ? (var95Pct < 0 ? 'negative' : 'neutral') : undefined} />
        <MetricBox label={`ES 95% (${range})`} value={es95Pct} fmt="pct2" toneOverride={es95Pct != null ? (es95Pct < 0 ? 'negative' : 'neutral') : undefined} />
        <MetricBox label={`Skewness (${range})`} value={skew} fmt="ratio2" />
        <MetricBox label={`Kurtosis (${range})`} value={kurt} fmt="ratio2" />
        {ticker.toUpperCase() !== 'BTC' && (
          <MetricBox label={`Crash Sensitivity (avg on worst 10 BTC days) (${range})`} value={crashSensitivityPct} fmt="pct2" toneBySign />
        )}
        <MetricBox label={`Median Recovery (days) (${range})`} value={medianRecoveryDays ?? null} fmt="int" />
      </div>
    </DataCard>
  );
};

export default TailMetricsPanel;
